import React, { useMemo, useState } from "react";
import {
  Box, Paper, Typography, LinearProgress, IconButton, Tooltip, alpha,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import ImageIcon from "@mui/icons-material/Image";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import {
  NAVY, NAVY_DARK, GOLD, BORDER, TEXT_PRIMARY, TEXT_SECONDARY,
} from "../../theme/tokens";
import { UploadTask, useUploads } from "./UploadContext";
import { formatBytes } from "../UploadFile/uploadMedia";

const STATUS_LABEL: Record<string, string> = {
  queued: "Queued",
  uploading: "Uploading",
  processing: "Processing on server",
  done: "Uploaded",
  error: "Failed",
  cancelled: "Cancelled",
};

const fileIcon = (mime: string) => {
  if (mime.startsWith("image/")) return <ImageIcon sx={{ fontSize: 16 }} />;
  if (mime.startsWith("video/")) return <OndemandVideoIcon sx={{ fontSize: 16 }} />;
  return <InsertDriveFileIcon sx={{ fontSize: 16 }} />;
};

const UploadRow: React.FC<{ task: UploadTask }> = ({ task }) => {
  const { cancel, dismiss } = useUploads();
  const inFlight =
    task.status === "uploading" ||
    task.status === "queued" ||
    task.status === "processing";

  const tint =
    task.status === "done" ? "#10b981" :
    task.status === "error" ? "#ef4444" :
    task.status === "cancelled" ? "#94a3b8" :
    task.status === "processing" ? GOLD :
    "#3b82f6";

  const bg =
    task.status === "done" ? "#ecfdf5" :
    task.status === "error" ? "#fef2f2" :
    task.status === "cancelled" ? "#f1f5f9" :
    task.status === "processing" ? "#fff7d6" :
    "#dbeafe";

  return (
    <Box sx={{
      px: 2, py: 1.5,
      borderTop: `1px solid ${BORDER}`,
      "&:first-of-type": { borderTop: "none" },
    }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
        <Box sx={{
          width: 28, height: 28, borderRadius: 1.5,
          bgcolor: bg, color: tint,
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}>
          {fileIcon(task.mimeType)}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{
            fontSize: 12.5, fontWeight: 700, color: NAVY,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }} title={task.fileName}>
            {task.fileName}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.25 }}>
            <Typography sx={{
              fontSize: 11, fontWeight: 600, color: tint,
            }}>
              {STATUS_LABEL[task.status]}
              {task.status === "uploading" && ` · ${task.progress}%`}
            </Typography>
            <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY }}>
              · {formatBytes(task.fileSize)}
            </Typography>
          </Box>
        </Box>
        {task.status === "done" && (
          <CheckCircleRoundedIcon sx={{ fontSize: 18, color: "#10b981" }} />
        )}
        {task.status === "error" && (
          <Tooltip title={task.error || "Upload failed"}>
            <ErrorRoundedIcon sx={{ fontSize: 18, color: "#ef4444" }} />
          </Tooltip>
        )}
        {task.status === "cancelled" && (
          <CancelRoundedIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
        )}
        {task.status === "processing" && (
          <HourglassBottomIcon sx={{ fontSize: 18, color: GOLD }} />
        )}
        {inFlight ? (
          <Tooltip title="Cancel">
            <IconButton size="small" onClick={() => cancel(task.id)}
              sx={{ color: "#94a3b8", "&:hover": { color: "#dc2626", bgcolor: "#fef2f2" } }}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Dismiss">
            <IconButton size="small" onClick={() => dismiss(task.id)}
              sx={{ color: "#94a3b8", "&:hover": { color: NAVY, bgcolor: "#f1f5f9" } }}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {(task.status === "uploading" || task.status === "queued" || task.status === "processing") && (
        <Box sx={{ mt: 1 }}>
          <LinearProgress
            variant={task.status === "processing" ? "indeterminate" : "determinate"}
            value={task.progress}
            sx={{
              height: 6, borderRadius: 3, bgcolor: "#f1f5f9",
              "& .MuiLinearProgress-bar": {
                borderRadius: 3,
                background:
                  task.status === "processing"
                    ? `linear-gradient(90deg, ${GOLD} 0%, #F5D97E 100%)`
                    : "linear-gradient(90deg, #3b82f6 0%, #60a5fa 100%)",
              },
            }} />
        </Box>
      )}

      {task.status === "error" && task.error && (
        <Typography sx={{
          fontSize: 11, color: "#b91c1c", mt: 0.75,
          bgcolor: "#fef2f2", p: 1, borderRadius: 1,
          border: "1px solid #fecaca",
        }}>
          {task.error}
        </Typography>
      )}
    </Box>
  );
};

/**
 * Bottom-right floating panel. Mounted once by <UploadProvider />.
 */
export const UploadsPanel: React.FC = () => {
  const { uploads, hasActive, clearFinished } = useUploads();
  const [collapsed, setCollapsed] = useState(false);

  const activeCount = useMemo(
    () => uploads.filter(u =>
      u.status === "uploading" || u.status === "queued" || u.status === "processing"
    ).length,
    [uploads]
  );
  const doneCount = useMemo(
    () => uploads.filter(u => u.status === "done").length,
    [uploads]
  );
  const errorCount = useMemo(
    () => uploads.filter(u => u.status === "error").length,
    [uploads]
  );

  if (uploads.length === 0) return null;

  return (
    <Paper elevation={0} sx={{
      position: "fixed",
      right: 16,
      bottom: 16,
      width: 360,
      maxWidth: "calc(100vw - 32px)",
      borderRadius: "14px",
      border: `1px solid ${BORDER}`,
      boxShadow: `0 18px 48px ${alpha(NAVY, 0.18)}`,
      bgcolor: "#fff",
      overflow: "hidden",
      zIndex: 1400,
    }}>
      {/* Header */}
      <Box sx={{
        background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_DARK} 100%)`,
        color: "#fff",
        px: 2, py: 1.25,
        display: "flex", alignItems: "center", gap: 1,
      }}>
        <Box sx={{
          width: 28, height: 28, borderRadius: 1.25,
          bgcolor: alpha(GOLD, 0.18),
          color: GOLD,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <HourglassBottomIcon sx={{ fontSize: 16 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 800 }}>
            {hasActive
              ? `Uploading ${activeCount} file${activeCount === 1 ? "" : "s"}`
              : errorCount > 0
                ? `${errorCount} upload${errorCount === 1 ? "" : "s"} failed`
                : `${doneCount} upload${doneCount === 1 ? "" : "s"} complete`}
          </Typography>
          <Typography sx={{ fontSize: 10.5, color: "rgba(255,255,255,0.6)" }}>
            {uploads.length} total
          </Typography>
        </Box>
        {!hasActive && (
          <Tooltip title="Clear finished">
            <IconButton size="small" onClick={clearFinished}
              sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.08)" } }}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title={collapsed ? "Expand" : "Collapse"}>
          <IconButton size="small" onClick={() => setCollapsed(v => !v)}
            sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.08)" } }}>
            {collapsed ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
          </IconButton>
        </Tooltip>
      </Box>

      {!collapsed && (
        <Box sx={{ maxHeight: 360, overflowY: "auto" }}>
          {uploads.map(t => <UploadRow key={t.id} task={t} />)}
        </Box>
      )}
    </Paper>
  );
};

export default UploadsPanel;
